/* ===== DEFENSIVE CSS RESET ===== */
/* Reset ALL inheritable CSS properties from host page (Shadow DOM still allows inheritance).
 * Applied to root containers only, component styles override via higher specificity. */
.sheet-content,
.responsive-sheet,
.sust-sheet,
.standalone-certification {
  /* Typography */
  font-family: var(--sustainability-font-family);
  font-size: medium;
  font-style: normal;
  font-variant: normal;
  font-weight: normal;
  line-height: normal;
  letter-spacing: normal;
  word-spacing: normal;
  text-align: left;
  text-indent: 0;
  text-transform: none;
  white-space: normal;
  word-break: normal;
  word-wrap: normal;
  hyphens: manual;

  /* Color */
  color: var(--body-text);

  /* Visibility */
  visibility: visible;

  /* Lists */
  list-style: none;
  list-style-type: none;
  list-style-position: outside;
  list-style-image: none;

  /* Other */
  cursor: auto;
  direction: ltr;
  quotes: auto;
}

/* ===== CSS VARIABLES ===== */
:root {
  /* Typography */
  --sustainability-font-family: "Amazon Ember", sans-serif;
  --font-size-xs: 12px;
  --font-size-sm: 13px;
  --font-size-base: 14px;
  --font-size-md: 16px;
  --font-size-lg: 18px;
  --font-size-xl: 20px;
  --line-height-xs: 16px;
  --line-height-sm: 19px;
  --line-height-base: 20px;
  --line-height-md: 1.25;
  --line-height-lg: 1.3;
  --line-height-xl: 1.4;
  --line-height-title: 1.225;
  --font-weight-bold: 700;

  /* Spacing */
  --spacing-1: 4px;
  --spacing-2: 8px;
  --spacing-3: 12px;
  --spacing-4: 16px;
  --spacing-5: 20px;
  --spacing-6: 24px;

  /* Component Sizes */
  --icon-size-base: 24px;
  --icon-size-sm: 32px;
  --icon-size-lg: 36px;
  --icon-size-standalone: 50px;
  --icon-size-standalone-dark: 58px;
  --carousel-item-width: 280px;
  --carousel-gap: 16px;
  --sheet-padding: 21px;
  --border-radius: 0.8rem;

  /* Layout & Positioning */
  --detail-view-padding: 14px;
  --learn-more-padding: 22px;
  --mobile-line-height-lg: 22px;
  --mobile-padding-sm: 10px;
  --mobile-card-padding-sm: 13px;
  --mobile-card-padding-md: 17px;

  /* Component Dimensions */
  --sheet-z-index: 1200;
  --sheet-min-width: 350px;
  --sheet-max-width: 400px;
  --mobile-sheet-max-height: 70vh;
  --mobile-sheet-min-height: 300px;
  --mobile-card-width: 80vw;

  /* Gaps & Spacing */
  --certification-gap-sm: 9px;
  --certification-gap-md: 15px;
  --border-radius-circle: 50%;

  /* Border & Outline */
  --border-width-thin: 1px;
  --border-width-thick: 2px;
  --border-width-mobile: 0.1rem;

  /* Typography */
  --font-weight-semibold: 600;
  --line-height-mobile-disclosure: 1.2858;

  /* Breakpoints */
  --breakpoint-mobile: 768px;

  /* Theme Colors - Light (default) */
  --primary-text: #232f3e;
  --secondary-text: #565959;
  --body-text: #0f1111;
  --link-color: #2162a1;
  --border-color: #d5d9d9;
  --border-color-alt: #e7e7e7;
  --hover-bg: rgba(0, 0, 0, 0.05);
  --focus-outline: #0073bb;
  --bg-white: #ffffff;
  --bg-transparent: transparent;

  /* Common Values */
  --transition-fast: 0.2s ease;
  --shadow-light: 0 2px 4px rgba(0, 0, 0, 0.1);
  /* Dark mode: #12B08C */
  --icon-filter-green: brightness(0) saturate(100%) invert(36%) sepia(68%)
    saturate(864%) hue-rotate(116deg) brightness(1.46) contrast(0.5);

  /* Backdrop & Overlay */
  --backdrop-color: rgba(0, 0, 0, 0.7);
}

/* Dark Theme Override */
.sustainability-theme-dark {
  --primary-text: #ffffff;
  --secondary-text: #cccccc;
  --body-text: #f0f0f0;
  --link-color: #4d9de0;
  --border-color: rgb(110, 119, 127);
  --hover-bg: rgba(255, 255, 255, 0.1);
  --bg-white: transparent;
  --bg-dark: rgb(14, 23, 29);
  --certification-icon-bg: #ffffff;
}

/* ===== CSS RESET FOR SHEET COMPONENTS ===== */
.sheet-content *,
.responsive-sheet * {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

/* ===== BASE STYLES ===== */
.sustainability-theme-light,
.sustainability-theme-dark {
  font-family: var(--sustainability-font-family);
}

/* ===== TYPOGRAPHY & TEXT COLORS ===== */
.sheet-header__title,
.feature-card__title,
.certification-detail-view__title,
.certification-detail-view__details-title {
  color: var(--body-text);
  font-weight: var(--font-weight-bold);
}

.sheet-header__description,
.feature-card__description,
.certification-detail-view__description,
.certification-detail-view__detail-heading,
.certification-detail-view__detail-value,
.sheet-footer__description {
  color: var(--body-text);
}

.feature-card__disclosure {
  color: var(--secondary-text);
}

/* ===== INTERACTIVE ELEMENTS ===== */
.btn-icon {
  background: none;
  border: none;
  cursor: pointer;
  color: var(--body-text);
  padding: var(--spacing-1);
  border-radius: var(--spacing-1);
  transition: var(--transition-fast);
}

.btn-icon:focus {
  outline: var(--border-width-thick) solid var(--focus-outline);
}

.certification-tile__name,
.sheet-footer__learn-more-link,
.certification-detail-view__learn-more-link {
  color: var(--link-color);
  text-decoration: none;
  font-size: var(--font-size-base);
  line-height: var(--line-height-base);
}

.sheet-footer__learn-more {
  color: var(--link-color);
}

.certification-tile:hover,
.certification-tile:hover .certification-tile__name,
.sheet-footer__learn-more-link:hover,
.sheet-footer__learn-more:hover,
.certification-detail-view__learn-more-link:hover {
  text-decoration: underline;
  text-decoration-color: currentColor;
}

/* ===== LAYOUT COMPONENTS ===== */

/* Feature Card */
.feature-card {
  padding: var(--spacing-3) 0;
}

.feature-card__description {
  font-size: var(--font-size-base);
  line-height: var(--line-height-base);
  margin-bottom: var(--spacing-2);
}

.feature-card__disclosure {
  margin-bottom: var(--spacing-1);
  font-size: var(--font-size-xs);
  line-height: var(--line-height-xs);
}

.feature-card__certifications {
  display: flex;
  flex-wrap: wrap;
  gap: var(--certification-gap-sm) var(--certification-gap-md);
  margin: 0;
  padding: 0;
  list-style: none; /* Defensive: prevent external ul styles */
}

/* Certification Tile */
.certification-tile__name {
  margin: 0;
  font-weight: normal;
}

.certification-tile {
  display: inline-flex;
  align-items: center;
  cursor: pointer;
  font-size: var(--font-size-base);
  line-height: var(--line-height-base);
  margin: 0;
  padding: 0;
}

.certification-tile__icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: calc(var(--icon-size-base) + var(--spacing-2));
  height: calc(var(--icon-size-base) + var(--spacing-2));
}

.certification-tile__icon img {
  width: var(--icon-size-base);
  height: var(--icon-size-base);
  object-fit: cover;
}

/* Mobile Features Carousel */
.features-carousel {
  width: 100%;
  overflow: hidden;
}

.features-carousel__container {
  display: flex;
  gap: var(--carousel-gap);
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  scrollbar-width: none;
  -ms-overflow-style: none;
  cursor: grab;
  touch-action: pan-x;
  -webkit-overflow-scrolling: touch;
  scroll-behavior: smooth;
  list-style: none;
  margin: 0;
}

.features-carousel__container::-webkit-scrollbar {
  display: none;
}

.features-carousel__container--dragging {
  cursor: grabbing;
  scroll-behavior: auto;
}

.features-carousel__container--dragging .features-carousel__card {
  pointer-events: none;
  user-select: none;
}

.features-carousel__item {
  scroll-snap-align: start;
  list-style: none; /* Defensive: prevent external ul li styles */
}

.features-carousel__card {
  height: 100%;
  box-shadow: var(--shadow-light);
}

/* Single item carousel centering */
.features-carousel--single .features-carousel__container {
  justify-content: center;
  overflow-x: visible;
  scroll-snap-type: none;
}

.features-carousel--single .features-carousel__item {
  scroll-snap-align: center;
  width: 100%;
}

/* Certification Detail View */
.certification-detail-view {
  padding-top: var(--detail-view-padding);
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.certification-detail-view__header {
  display: flex;
  align-items: center;
  gap: var(--spacing-4);
  margin-bottom: var(--spacing-5);
}

.certification-detail-view__icon {
  width: var(--icon-size-sm);
  height: var(--icon-size-sm);
  flex-shrink: 0;
  object-fit: contain;
}

.certification-detail-view__title {
  font-size: var(--font-size-lg);
  font-weight: 400;
  line-height: var(--line-height-title);
}

.certification-detail-view__content {
  flex: 1;
  overflow-y: auto;
  min-height: 0;
}

.certification-detail-view__description {
  margin-bottom: var(--detail-view-padding);
}

.certification-detail-view__details {
  margin-bottom: var(--spacing-5);
}

.certification-detail-view__details-title {
  margin: 0 0 var(--spacing-3) 0;
}

.certification-detail-view__detail-row {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-1);
  margin-bottom: var(--spacing-4);
}

.certification-detail-view__detail-value {
  margin-bottom: var(--spacing-2);
}

/* Expander Styles */
.features-expander {
  list-style: none;
  margin: 0;
  padding: 0;
}

.features-expander__card .feature-card__title {
  display: none;
}

.features-expander__item {
  border-color: var(--border-color);
  list-style: none; /* Defensive: prevent external ul li styles */
}

/* Custom Accessible Expander Styles */
.sust-expander {
  border-bottom: var(--border-width-thin) solid var(--border-color);
}

.sust-expander__heading {
  margin: 0;
  padding: 0;
}

.sust-expander__button {
  width: 100%;
  display: flex;
  align-items: center;
  gap: var(--spacing-2);
  padding: var(--spacing-4);
  background: none;
  border: none;
  cursor: pointer;
  font-family: var(--sustainability-font-family);
  font-size: var(--font-size-md);
  line-height: var(--line-height-title);
  font-weight: var(--font-weight-bold);
  color: var(--primary-text);
  text-align: left;
  transition: background-color var(--transition-fast);
}

.sust-expander__button:hover {
  background: var(
    --bg-white
  ); /* Defensive: prevent external button:hover overrides */
}

.sust-expander__button:focus {
  outline: var(--border-width-thick) solid var(--focus-outline);
  outline-offset: -2px;
}

.sust-expander__icon {
  flex-shrink: 0;
}

.sust-expander__region {
  padding: 0 var(--spacing-4) var(--spacing-4);
  animation: slideDown var(--transition-fast) ease-out;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Base Sheet Styles (Common to all breakpoints) */
.responsive-sheet {
  z-index: var(--sheet-z-index);
}

.sustainability-theme-light {
  background-color: var(--bg-white);
}

.sustainability-theme-dark {
  background-color: var(--bg-dark);
}

.sheet-content {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
}

.sheet-content__main {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
}

.sheet-content__detail-view {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
}

.sheet-header__content {
  display: flex;
  align-items: center;
}

.sheet-header__back {
  margin-right: var(--spacing-2);
}

.sheet-header__title {
  display: flex;
  align-items: center;
}

.sheet-header__title-section {
  display: flex;
  align-items: center;
  flex: 1;
}

.sheet-header__leaf-icon {
  width: var(--icon-size-base);
  height: var(--icon-size-base);
  vertical-align: top;
}

.sheet-header__actions {
  margin-left: auto;
}

.features-display--mobile {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.sheet-footer__icon {
  flex-shrink: 0;
}

.certification-detail-view {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.sheet-footer__content--link {
  text-decoration: none !important;
}

/* Desktop-Specific Styles */
.responsive-sheet--desktop .sheet-content {
  width: fit-content;
  min-width: var(--sheet-min-width);
  max-width: var(--sheet-max-width);
  padding: var(--sheet-padding);
}

.responsive-sheet--desktop .sheet-header {
  border-bottom: var(--border-width-thin) solid var(--border-color);
}

.responsive-sheet--desktop .sheet-header__content {
  margin-bottom: var(--spacing-2);
}

.responsive-sheet--desktop .sheet-header__icon {
  margin-right: var(--spacing-2);
}

.responsive-sheet--desktop .sheet-header__title {
  font-size: var(--font-size-xl);
  line-height: var(--line-height-xl);
  gap: var(--spacing-2);
}
.responsive-sheet--desktop .certification-detail-view__learn-more {
  margin: 0 0 var(--detail-view-padding) 0;
  padding-bottom: var(--learn-more-padding);
}

.responsive-sheet--desktop .certification-detail-view__learn-more--has-details {
  border-bottom: var(--border-width-thin) solid var(--border-color);
}

.responsive-sheet--desktop .sheet-header__description {
  margin-bottom: var(--spacing-4);
  font-size: var(--font-size-base);
  line-height: var(--line-height-base);
}

.responsive-sheet--desktop .feature-card__description,
.responsive-sheet--desktop .sheet-footer__description,
.responsive-sheet--desktop .sheet-footer__learn-more {
  font-size: var(--font-size-base);
  line-height: var(--line-height-base);
}

.responsive-sheet--desktop .sheet-content__main {
  padding-bottom: var(--spacing-3);
}

.responsive-sheet--desktop .sheet-footer {
  text-align: left;
  margin: 0 -15px;
  padding: 0 15px;
  border-top-color: var(--border-color);
}

.responsive-sheet--desktop .certification-detail-view__description,
.responsive-sheet--desktop .certification-detail-view__learn-more,
.responsive-sheet--desktop .certification-detail-view__detail-heading,
.responsive-sheet--desktop .certification-detail-view__detail-value {
  font-size: var(--font-size-base);
  line-height: var(--line-height-base);
}

/* Mobile-Specific Styles */
.responsive-sheet--mobile {
  max-height: 70vh;
  height: 100%;
}

.responsive-sheet--mobile .sheet-header__content {
  padding-top: var(--spacing-4);
  padding-bottom: var(--spacing-1);
}

.responsive-sheet--mobile .sheet-header__title {
  font-size: var(--font-size-lg);
  line-height: var(--mobile-line-height-lg);
  gap: var(--spacing-3);
}

.responsive-sheet--mobile .sheet-header__leaf-icon {
  margin-right: var(--spacing-2);
}

.responsive-sheet--mobile .sheet-header__description {
  padding-top: var(--mobile-padding-sm);
  padding-bottom: var(--spacing-4);
  font-size: var(--font-size-md);
  line-height: var(--line-height-md);
}

.responsive-sheet--mobile .sheet-content {
  padding: 0 var(--spacing-4);
  height: 100%;
}

.responsive-sheet--mobile .features-display--mobile {
  padding: 0;
}

/* Single item carousel - full width with proper padding */
.responsive-sheet--mobile .features-carousel--single {
  margin-left: 0;
  margin-right: 0;
  padding-left: 0;
}

.responsive-sheet--mobile .features-carousel--single .features-carousel__card {
  width: 100%;
}

.responsive-sheet--mobile .features-carousel__card {
  border-radius: var(--border-radius);
  background-color: var(--bg-white);
  border: var(--border-width-mobile) solid var(--border-color);
  width: var(--mobile-card-width);
  padding: var(--mobile-card-padding-sm) var(--mobile-card-padding-md);
}

.responsive-sheet--mobile .feature-card {
  border-bottom: var(--border-width-thin) solid var(--border-color-alt);
  display: flex;
  flex-direction: column;
  height: 100%;
}

.responsive-sheet--mobile .feature-card__description {
  flex: 1;
}

.responsive-sheet--mobile .feature-card__disclosure {
  margin-top: auto;
}

.responsive-sheet--mobile .feature-card__title,
.responsive-sheet--mobile .feature-card__description,
.responsive-sheet--mobile .certification-tile,
.responsive-sheet--mobile .certification-detail-view__description,
.responsive-sheet--mobile .certification-detail-view__detail-heading,
.responsive-sheet--mobile .certification-detail-view__learn-more-link,
.responsive-sheet--mobile .sheet-footer__info,
.responsive-sheet--mobile .certification-detail-view__detail-value {
  font-size: var(--font-size-md);
  line-height: var(--line-height-md);
}

.responsive-sheet--mobile .feature-card__description {
  margin: var(--spacing-1) 0;
}

.responsive-sheet--mobile .feature-card__disclosure {
  font-size: var(--font-size-base);
  line-height: var(--line-height-mobile-disclosure);
  margin-bottom: var(--spacing-1);
}

.responsive-sheet--mobile
  .sheet-header--certification-detail
  .sheet-header__content {
  border-bottom: var(--border-width-thin) solid var(--border-color);
}

.responsive-sheet--mobile .certification-detail-view__description {
  margin-bottom: var(--mobile-card-padding-sm);
}

.responsive-sheet--mobile .sheet-footer__icon {
  padding-top: var(--spacing-3);
}

.responsive-sheet--mobile .sheet-footer__icon h3 {
  font-weight: var(--font-weight-bold);
  font-size: var(--font-size-lg);
  line-height: var(--line-height-title);
}

.responsive-sheet--mobile .sheet-footer__info {
  padding-bottom: var(--mobile-padding-sm);
}

.responsive-sheet--mobile .certification-detail-view__learn-more {
  margin: 0 0 var(--detail-view-padding) 0;
  padding-bottom: 0;
}

.responsive-sheet--mobile .certification-detail-view__learn-more--has-details {
  border-bottom: var(--border-width-thin) solid var(--border-color);
}

/* ===== DARK THEME SPECIFIC OVERRIDES ===== */
.sustainability-theme-dark .sheet-header__leaf-icon,
.sustainability-theme-dark .sheet-footer__program-icon {
  filter: var(--icon-filter-green);
}

/* Certification Icon Dark Mode Styling */
.sustainability-theme-dark .certification-tile__icon,
.sustainability-theme-dark .certification-detail-view__icon {
  background-color: var(--certification-icon-bg);
  border-radius: var(--border-radius-circle);
  overflow: hidden;
  padding: var(--spacing-1);
  box-sizing: border-box;
  flex-shrink: 0;
}

.sustainability-theme-dark .certification-tile__icon img,
.sustainability-theme-dark .certification-detail-view__icon {
  object-fit: cover;
  border-radius: var(--border-radius-circle);
}

.sustainability-theme-dark .certification-tile__name {
  padding-left: var(--spacing-2);
}

.sustainability-theme-dark.responsive-sheet--mobile .features-carousel__card {
  background-color: var(--bg-transparent);
  border-color: var(--border-color) !important;
}

.sustainability-theme-dark.responsive-sheet--mobile .feature-card {
  border-bottom-color: var(--border-color) !important;
  background-color: var(--bg-transparent);
}

.sustainability-theme-dark .features-expander__item:first-child {
  border-top: 1px solid var(--border-color);
}

/* ===== SUST SHEET CONTENT STYLES ===== */
/* Note: Positioning is now handled in light DOM wrapper for proper z-index stacking */
.sust-sheet {
  width: 100%;
  height: 100%;
  overflow-y: auto;
}

.sust-sheet--end {
  overflow-y: auto;
}

.sust-sheet--end.responsive-sheet--desktop {
  height: 100% !important;
}

.sust-sheet--bottom {
  overflow-y: auto;
}

/* Hide sheets that are not visible to prevent flash during resize */
.sust-sheet--hidden {
  display: none !important;
}

/* ===== STANDALONE CERTIFICATION STYLES ===== */
.standalone-certification {
  padding: var(--spacing-4) 0;
}

.standalone-certification__title {
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-bold);
  line-height: var(--line-height-title);
  color: var(--body-text);
  margin-bottom: var(--spacing-3);
}

.standalone-certification__item:not(:last-child) {
  margin-bottom: var(--spacing-5);
  padding-bottom: var(--spacing-5);
  border-bottom: var(--border-width-thin) solid var(--border-color);
}

.standalone-certification__card {
  display: flex;
  align-items: center;
  gap: var(--spacing-4);
}

.standalone-certification__icon {
  flex-shrink: 0;
  min-width: var(--icon-size-standalone);
  cursor: pointer;
  border-radius: var(--spacing-1);
  transition: all var(--transition-fast);
}

.standalone-certification__icon:hover {
  opacity: 0.8;
  box-shadow: var(--shadow-light);
}

.standalone-certification__icon:active {
  opacity: 0.7;
  transform: scale(0.95);
}

.standalone-certification__icon:focus {
  outline: var(--border-width-thick) solid var(--focus-outline);
  outline-offset: var(--spacing-1);
}

.standalone-certification__icon-image {
  width: var(--icon-size-standalone);
  height: var(--icon-size-standalone);
  object-fit: contain;
}

.standalone-certification__description {
  font-size: var(--font-size-base);
  line-height: var(--line-height-base);
  color: var(--body-text);
  flex: 1;
}

/* Mobile adjustments */
.responsive-sheet--mobile .standalone-certification__title {
  font-size: var(--font-size-md);
  line-height: var(--mobile-line-height-lg);
}

.responsive-sheet--mobile .standalone-certification__description {
  font-size: var(--font-size-md);
  line-height: var(--line-height-md);
}

/* Dark mode icon styling */
.sustainability-theme-dark .standalone-certification__icon {
  background-color: var(--certification-icon-bg);
  border-radius: var(--border-radius-circle);
  width: var(--icon-size-standalone-dark);
  height: var(--icon-size-standalone-dark);
  padding: var(--spacing-1);
}

.sustainability-theme-dark .standalone-certification__icon-image {
  border-radius: var(--border-radius-circle);
}
